import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiSearch, FiUsers } from "react-icons/fi";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { makeRequestAuth } from "../../_config/api";
import { showErrorToast } from "../../utils/toastUtils";
import Spinner from "../../utils/Loader";

function ReferralChainPage() {
  const { userId, userName } = useParams();
  const navigate = useNavigate();

  const [referrals, setReferrals] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const decodedName = decodeURIComponent(userName || "User");

  const fetchReferralChain = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const response = await makeRequestAuth(
          "get",
          `/admin/getReferralChainList?userId=${userId}&search=${searchTerm}&page=${page}&limit=${pageSize}`
        );

        console.log("RAW response:", response);

        // makeRequestAuth ke hisaab se alag alag structure ho sakta hai:
        // Case 1: response = { data: { message: { total, data: [] } } }
        // Case 2: response = { message: { total, data: [] } }
        // Case 3: response = { total, data: [] }  (already unwrapped)

        let msgData = null;

        if (response?.data?.message?.data) {
          msgData = response.data.message;
        } else if (response?.message?.data) {
          msgData = response.message;
        } else if (response?.data?.data) {
          msgData = response.data;
        } else if (Array.isArray(response?.data)) {
          setReferrals(response.data);
          setTotal(response.data.length);
          return;
        }

        if (msgData && Array.isArray(msgData.data)) {
          setReferrals(msgData.data);
          setTotal(msgData.total || 0);
        } else {
          console.warn("Could not parse response:", response);
          setReferrals([]);
          setTotal(0);
        }
      } catch (err) {
        console.error("Error:", err);
        showErrorToast(err.message || "Failed to fetch referral chain");
      } finally {
        setIsLoading(false);
      }
    },
    [userId, searchTerm, pageSize]
  );

  useEffect(() => {
    const delay = setTimeout(() => { fetchReferralChain(currentPage); }, 400);
    return () => clearTimeout(delay);
  }, [currentPage, searchTerm, fetchReferralChain]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(Number(timestamp)).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const totalPages = Math.ceil(total / pageSize);
  const goToPage = (page) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };

  if (isLoading) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-6 p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white dark:bg-[#1E2738] text-gray-800 dark:text-white rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)}
              className="p-2 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
              <FiArrowLeft size={20} className="text-gray-600 dark:text-gray-300" />
            </button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {decodedName.charAt(0).toUpperCase()}
                </div>
                <span className="text-base font-semibold text-gray-700 dark:text-gray-300">{decodedName}</span>
                <span className="px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs font-semibold">
                  Referral Chain
                </span>
              </div>
              <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Referral Network
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Total {total} referrals found</p>
            </div>
          </div>

          <div className="relative w-full sm:w-64">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search referrals..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition" />
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-700">
                  <th className="py-4 px-6 w-16 text-center">#</th>
                  <th className="py-4 px-6 min-w-[180px]">Name</th>
                  <th className="py-4 px-6 min-w-[150px]">Username</th>
                  <th className="py-4 px-6 min-w-[200px]">Email</th>
                  <th className="py-4 px-6 min-w-[160px]">Mobile</th>
                  <th className="py-4 px-6 min-w-[130px]">Referral Code</th>
                  <th className="py-4 px-6 min-w-[130px]">Ref By Code</th>
                  <th className="py-4 px-6 min-w-[130px]">Joined Date</th>
                  <th className="py-4 px-6 min-w-[100px]">KYC</th>
                  <th className="py-4 px-6 min-w-[100px]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {referrals.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-16 text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className="h-16 w-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          <FiUsers size={28} className="text-gray-400" />
                        </div>
                        <p className="text-lg font-medium">No Referrals Found</p>
                        <p className="text-sm">This user has not referred anyone yet.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  referrals.map((ref, index) => (
                    <tr key={ref._id} className="hover:bg-blue-50/50 dark:hover:bg-gray-700/30 transition">
                      <td className="py-4 px-6 text-center text-gray-500 dark:text-gray-400 font-medium">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 flex-shrink-0">
                            {ref.image ? (
                              <img src={ref.image} alt={ref.name} className="h-full w-full object-cover"
                                onError={(e) => { e.target.src = "https://ui-avatars.com/api/?name=" + ref.name; }} />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-500 text-white font-bold text-xs">
                                {ref.name?.charAt(0).toUpperCase() || "U"}
                              </div>
                            )}
                          </div>
                          <span className="text-gray-900 dark:text-white font-semibold line-clamp-1">{ref.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm font-mono">{ref.userName || "—"}</td>
                      <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">{ref.email || "—"}</td>
                      <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">{ref.mobile || "—"}</td>
                      <td className="py-4 px-6">
                        <span className="px-3 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 font-mono text-sm font-semibold">
                          {ref.referralCode || "—"}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="px-3 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-mono text-sm font-semibold">
                          {ref.refByCode || "—"}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">{formatDate(ref.createdAt)}</td>
                      <td className="py-4 px-6">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${ref.isKyc ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400" : "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"}`}>
                          {ref.isKyc ? "Verified" : "Pending"}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${!ref.isDisable ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400" : "bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400"}`}>
                          {!ref.isDisable ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        {total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
              Showing <span className="text-gray-900 dark:text-white font-bold">{(currentPage - 1) * pageSize + 1}</span> to{" "}
              <span className="text-gray-900 dark:text-white font-bold">{Math.min(currentPage * pageSize, total)}</span> of{" "}
              <span className="text-gray-900 dark:text-white font-bold">{total}</span> entries
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">
                Previous
              </button>
              <div className="hidden sm:flex gap-1">
                {[...Array(totalPages).keys()].slice(0, 5).map((n) => (
                  <button key={n + 1} onClick={() => goToPage(n + 1)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold transition ${currentPage === n + 1 ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md" : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"}`}>
                    {n + 1}
                  </button>
                ))}
              </div>
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReferralChainPage;